import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// --- ALLOWED ICONS ONLY ---
// heart, book, handshake, calendar, user, phone, map-pin

const CalendarIcon = ({ className = "h-6 w-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const UserIcon = ({ className = "h-6 w-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const BookIcon = ({ className = "h-6 w-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

const HeartIcon = ({ className = "h-6 w-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const MapPinIcon = ({ className = "h-6 w-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const PhoneIcon = ({ className = "h-6 w-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const HomePage = () => {
  // Quick Access Links
  const quickLinks = [
    { name: 'Calendar', icon: CalendarIcon, href: 'https://www.schools.nyc.gov/calendar/2026-2027-school-year-calendar' },
    { name: 'Parent Portal', icon: UserIcon, href: 'https://www.schoolsaccount.nyc/' },
    { name: 'Staff Directory', icon: UserIcon, href: '#staff' },
    { name: 'Supply Lists', icon: BookIcon, href: '#supplies' },
    { name: 'Enroll', icon: BookIcon, href: 'https://docs.google.com/forms/d/e/1FAIpQLSejo-4-MnLhbXjOYyW0ahVgWAZ7iytPNQ94w46b2zXGX0gD4w/viewform?usp=dialog' },
    { name: 'ParentSquare', icon: PhoneIcon, href: 'https://www.parentsquare.com/' },
  ];

  // Staff Information
  const keyStaff = [
    { name: 'Michael Panetta', role: 'Principal', email: 'mpanetta@schools.nyc.gov' },
    { name: 'Ayo Mendez-Torres', role: 'Assistant Principal', email: 'amendeztorres@schools.nyc.gov' },
    { name: 'Raquel Rosas', role: 'Parent Coordinator', email: 'RRosas3@schools.nyc.gov' },
    { name: 'Maura Kyne', role: 'Guidance Counselor', email: 'msmaura@rivereastelementary.org' },
  ];

  // Full Staff Directory (2026-2027)
  const staffDirectory = [
    {
      department: 'Administration',
      members: [
        { role: 'Principal', name: 'Michael Panetta', room: 'Rm. 107' },
        { role: 'Assistant Principal', name: 'Ayo Mendez Torres', room: 'Rm. 135B' },
      ],
    },
    {
      department: 'Classroom Positions',
      members: [
        { role: 'Pre-K 3', name: 'Claudia Felth and Jacqueline Rivers', room: 'Rm. 146' },
        { role: 'Pre-K 3', name: 'Monica Rosas and Elizabeth Canela', room: 'Rm. 151' },
        { role: 'Pre-K 4', name: 'Jordan Reneau and Samantha Torres', room: 'Rm. 140' },
        { role: 'Pre-K 4', name: 'Jeraldin Rosario-Alvarado and Glennys Soto', room: 'Rm. 127' },
        { role: 'Kindergarten ICT', name: 'Chelsea Attride and Abbi Magder', room: 'Rm. 145' },
        { role: 'Kindergarten', name: 'Berta Gomez', room: 'Rm. 134' },
        { role: '1st Grade ICT', name: 'Eileen Raghunath and Tessa Rudd', room: 'Rm. 136' },
        { role: '1st Grade', name: 'Bernadette Acevedo', room: 'Rm. 102' },
        { role: '2nd Grade ICT', name: 'Louise Dominado and Margaret Duggan', room: 'Rm. 132' },
        { role: '2nd Grade', name: 'Amanda Ramos', room: 'Rm. 135' },
        { role: '3rd Grade ICT', name: 'Joyce Malcolm and Anne Montero', room: 'Rm. 239' },
        { role: '3rd Grade', name: 'Chloe Hamlin', room: 'Rm. 237' },
        { role: '4th Grade ICT', name: 'Katie Chan and Alyssa Skidmore', room: 'Rm. 238' },
        { role: '4th Grade', name: 'Jacqueline Kaishian', room: 'Rm. 236' },
        { role: '5th Grade ICT', name: 'Aaron Yau', room: 'Rm. 249' },
        { role: '5th Grade', name: 'Abdul Howard', room: 'Rm. 251' },
      ],
    },
    {
      department: 'Paraprofessionals',
      members: [
        { role: 'Paraprofessional', name: 'Melanie Castillo-Robles' },
        { role: 'Paraprofessional', name: 'Joi Edwards' },
        { role: 'Paraprofessional', name: 'Jose Guzman' },
        { role: 'Paraprofessional', name: 'Eugenia Page' },
        { role: 'Paraprofessional', name: 'Andriena Thorpe' },
      ],
    },
    {
      department: 'Clusters',
      members: [
        { role: 'Art', name: 'Erin Bockler', room: 'Rm. 148' },
        { role: 'Physical Education', name: 'Stanley Maxi', room: 'Gym' },
        { role: 'Science', name: 'Laura Ralph', room: 'Rm. 149' },
      ],
    },
    {
      department: 'Student Support Services',
      members: [
        { role: 'ENL', name: 'Sara Khan', room: 'Rm. 133' },
        { role: 'SETSS', name: 'Merav Naor', room: 'Rm. 139' },
      ],
    },
    {
      department: 'School Based Support Team',
      room: 'Rm. 104',
      members: [
        { role: 'Psychologist', name: 'Helena Tyson' },
        { role: 'Social Worker', name: 'Yesenia Diaz-Lopez' },
        { role: 'Family Worker', name: 'Stephanie Aloy' },
      ],
    },
    {
      department: 'Related Services',
      members: [
        { role: 'Guidance', name: 'Maura Kyne', room: 'Rm. 240' },
        { role: 'Occupational Therapy', name: 'Stephanie Gilbert', room: 'Rm. 108' },
        { role: 'Physical Therapy', name: 'Monica Popa', room: 'Rm. 108' },
        { role: 'Speech', name: 'Allie Segat', room: 'Rm. 108' },
      ],
    },
    {
      department: 'Office Staff',
      room: 'Rm. 117',
      members: [
        { role: 'Parent Coordinator', name: 'Raquel Rosas' },
        { role: 'Secretary', name: 'Tara Cadell-Weiters' },
      ],
    },
  ];

  // Values
  const values = [
    {
      icon: <BookIcon className="h-8 w-8" />,
      title: 'Academic Excellence',
      description: 'Double-digit gains on ELA and Math State exams demonstrate our commitment to student success.',
    },
    {
      icon: <HeartIcon className="h-8 w-8" />,
      title: 'Community First',
      description: 'A small, tight-knit school community where every student is known, valued, and supported.',
    },
    {
      icon: <UserIcon className="h-8 w-8" />,
      title: 'Dedicated Staff',
      description: 'Experienced educators and support staff committed to nurturing every child\'s potential.',
    },
  ];

  // Statistics
  const stats = [
    { value: 'PK3-5th', label: 'Grade Levels' },
    { value: '8:20 AM', label: 'School Starts' },
    { value: '2:40 PM', label: 'Dismissal' },
    { value: '6:00 PM', label: 'After School' },
  ];

  // Announcements
  const announcements = [];

  // Welcome Letters & Supply Lists (2026-2027)
  const docsBase = '/assets/documents/welcome-supply';
  const gradeMaterials = [
    { grade: 'Pre-K 3', docs: [
      { label: 'Welcome Letter', href: `${docsBase}/prek3-welcome-letter.pdf` },
      { label: 'Supply List', href: `${docsBase}/prek3-supply-list.pdf` },
    ] },
    { grade: 'Pre-K 4', docs: [
      { label: 'Welcome Letter', href: `${docsBase}/prek4-welcome-letter.pdf` },
      { label: 'Supply List', href: `${docsBase}/prek4-supply-list.pdf` },
    ] },
    { grade: 'Kindergarten', docs: [
      { label: 'Welcome Letter & Supply List', href: `${docsBase}/kindergarten-welcome-supply.pdf` },
    ] },
    { grade: '1st Grade', docs: [
      { label: 'Welcome Letter & Supply List', href: `${docsBase}/grade1-welcome-supply.pdf` },
    ] },
    { grade: '2nd Grade', docs: [
      { label: 'Welcome Letter', href: `${docsBase}/grade2-welcome-letter.pdf` },
      { label: 'Supply List', href: `${docsBase}/grade2-supply-list.pdf` },
    ] },
    { grade: '3rd Grade', docs: [
      { label: 'Welcome Letter', href: `${docsBase}/grade3-welcome-letter.pdf` },
      { label: 'Supply List', href: `${docsBase}/grade3-supply-list.pdf` },
    ] },
    { grade: '4th Grade', docs: [
      { label: 'Welcome Letter', href: `${docsBase}/grade4-welcome-letter.pdf` },
      { label: 'Supply List', href: `${docsBase}/grade4-supply-list.pdf` },
    ] },
    { grade: '5th Grade', docs: [
      { label: 'Welcome Letter', href: `${docsBase}/grade5-welcome-letter.pdf` },
      { label: 'Supply List', href: `${docsBase}/grade5-supply-list.pdf` },
    ] },
  ];

  // Events
  const events = [
    {
      date: { month: 'Sep', day: '3' },
      title: 'Virtual Parent Orientation',
      description: '',
      time: '',
    },
    {
      date: { month: 'Sep', day: '9' },
      title: 'In-Person Open House',
      description: '',
      time: '',
    },
    {
      date: { month: 'Sep', day: '10' },
      title: 'First Day of School',
      description: '',
      time: '',
    },
    {
      date: { month: 'Sep', day: '14' },
      title: 'Beacon After-School Program Begins',
      description: '',
      time: '',
    },
    {
      date: { month: 'Sep', day: '30' },
      title: 'In-Person Curriculum Night',
      description: '',
      time: '',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 font-sans selection:bg-cyan-200 selection:text-blue-900">
      <Navbar />

      <main id="main-content">
        {/* Hero Section - Cinematic & Minimal */}
        <section className="relative flex min-h-[90vh] items-center justify-center overflow-hidden bg-blue-900">
          {/* Background with Gradient Overlay */}
          <div className="absolute inset-0 z-0">
             <video
              // React sets `muted` as a DOM property only, so it never lands in the
              // rendered/prerendered markup (browsers can then attempt an unmuted
              // autoplay before hydration, and axe's no-autoplay-audio check sees an
              // unmuted video). Setting the real attribute via ref makes it explicit
              // in the static HTML too.
              ref={(el) => { if (el) { el.muted = true; el.setAttribute('muted', ''); } }}
              src="/assets/videos/home/river east.mov"
              autoPlay
              muted
              loop
              playsInline
              aria-hidden="true"
              className="h-full w-full object-cover"
            />
            {/* Darkened enough that hero text clears AA contrast even against a bright
                frame of the looping video (verified by hand; axe can't measure video). */}
            <div className="absolute inset-0 bg-black/65" />
          </div>

          {/* Content */}
          <div className="relative z-10 mx-auto max-w-7xl px-6 py-32 text-center sm:px-8 lg:px-12 lg:text-left">
            <div className="max-w-3xl">
              <h1 className="text-5xl font-bold tracking-tighter text-white sm:text-6xl md:text-7xl lg:text-8xl">
                River East <br />
                <span className="text-cyan-400">Elementary</span>
            </h1>
              <p className="mt-6 text-xl font-medium leading-relaxed text-blue-50 sm:text-2xl">
                A small PK3-5th Grade school in East Harlem where every student is known, valued, and inspired.
              </p>
              
              <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row lg:justify-start">
              <a
                href="mailto:mpanetta@schools.nyc.gov"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-blue-900 shadow-xl transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                Schedule a Visit
              </a>
              <a
                href="#about"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                Learn about our school
              </a>
            </div>
          </div>
          </div>
        </section>

        {/* Quick Access - Floating Dock */}
        <section className="relative z-20 -mt-12 px-4 sm:px-6 pb-12">
          <div className="mx-auto max-w-5xl">
            <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-6 rounded-3xl bg-white px-8 py-6 shadow-2xl ring-1 ring-gray-200/50">
              {quickLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="group flex items-center gap-3 text-sm font-bold text-gray-700 transition-all hover:text-blue-700"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600 transition-all group-hover:bg-blue-600 group-hover:text-white group-hover:scale-110 shadow-sm">
                    <link.icon className="h-5 w-5" />
                  </div>
                  <span>{link.name}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Spider-Man Visit Video */}
        <section className="relative overflow-hidden bg-gradient-to-b from-red-950 via-red-900 to-blue-950 py-20 sm:py-24">
          {/* Spiderweb corner accents */}
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 text-white/10"
            viewBox="0 0 200 200"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          >
            <circle cx="0" cy="0" r="50" />
            <circle cx="0" cy="0" r="90" />
            <circle cx="0" cy="0" r="130" />
            <circle cx="0" cy="0" r="170" />
            {[0, 15, 30, 45, 60, 75, 90].map((deg) => (
              <line
                key={deg}
                x1="0"
                y1="0"
                x2={200 * Math.cos((deg * Math.PI) / 180)}
                y2={200 * Math.sin((deg * Math.PI) / 180)}
              />
            ))}
          </svg>
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-20 -right-20 h-72 w-72 rotate-180 text-white/10"
            viewBox="0 0 200 200"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          >
            <circle cx="0" cy="0" r="50" />
            <circle cx="0" cy="0" r="90" />
            <circle cx="0" cy="0" r="130" />
            <circle cx="0" cy="0" r="170" />
            {[0, 15, 30, 45, 60, 75, 90].map((deg) => (
              <line
                key={deg}
                x1="0"
                y1="0"
                x2={200 * Math.cos((deg * Math.PI) / 180)}
                y2={200 * Math.sin((deg * Math.PI) / 180)}
              />
            ))}
          </svg>

          <div className="relative mx-auto max-w-5xl px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center mb-10">
              <span className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-lg shadow-red-950/40 ring-1 ring-white/20">
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v20M2 12h20M4.5 4.5l15 15M19.5 4.5l-15 15M7 2c1 3 1 7 5 7s4-4 5-7M7 22c1-3 1-7 5-7s4 4 5 7" />
                </svg>
                Special Guest
              </span>
              <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                See <span className="text-red-400">Spider-Man</span> Tom Holland Visit River East
              </h2>
              <p className="mt-4 text-lg leading-8 text-blue-100/80">
                Your friendly neighborhood Avenger swung by to surprise our students &mdash; watch the moment below!
              </p>
            </div>
            <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl bg-gradient-to-br from-red-600 via-white to-blue-700 p-1 shadow-2xl shadow-black/40">
              <div className="relative w-full overflow-hidden rounded-[14px] bg-black" style={{ paddingTop: '56.25%' }}>
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src="https://www.youtube.com/embed/JpeBklOupFM"
                  title="Tom Holland Visits River East Elementary"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* Welcome Letters & Supply Lists Bar */}
        <section id="supplies" className="bg-white py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center mb-12">
              <span className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-cyan-700 ring-1 ring-cyan-100">
                <BookIcon className="h-4 w-4" />
                Back to School
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                Welcome Letters &amp; Supply Lists
              </h2>
              <p className="mt-4 text-lg leading-8 text-gray-600">
                Find your child's grade below to download the welcome letter and supply list for the new school year.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {gradeMaterials.map((item) => (
                <div
                  key={item.grade}
                  className="flex flex-col rounded-2xl border border-gray-200 bg-gray-50 p-6 transition-all hover:-translate-y-1 hover:border-blue-200 hover:bg-white hover:shadow-lg"
                >
                  <div className="mb-5 flex items-center gap-3">
                    <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
                      <BookIcon className="h-5 w-5" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">{item.grade}</h3>
                  </div>
                  <ul className="mt-auto space-y-2.5">
                    {item.docs.map((doc) => (
                      <li key={doc.label}>
                        <a
                          href={doc.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${item.grade} ${doc.label} (PDF, opens in a new tab)`}
                          className="group flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 transition-colors hover:bg-blue-50 hover:text-blue-800 hover:ring-blue-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        >
                          <span>{doc.label}</span>
                          <span className="flex items-center gap-1 text-xs font-bold text-blue-600">
                            PDF <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">&darr;</span>
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section - Asymmetric Layout */}
        <section id="about" className="overflow-x-clip py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="grid gap-16 lg:grid-cols-12 lg:gap-[96px] items-center">
              <div className="lg:col-span-5 order-2 lg:order-1">
                <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                  Small Community, <br />
                  <span className="text-blue-700">Big Impact</span>
                </h2>
                <p className="mt-6 text-lg leading-8 text-gray-600">
                  Located in the heart of East Harlem, River East Elementary (PS 37) serves students from Pre-K through 5th grade.
                  Our intimate environment ensures that every child receives personalized attention.
                </p>
                <p className="mt-4 text-lg leading-8 text-gray-600">
                  We're proud of our students' achievements, with double-digit gains on state exams and a curriculum that balances
                  academic rigor with social-emotional learning.
                </p>
                <div className="mt-8">
                  <a href="mailto:mpanetta@schools.nyc.gov" className="text-sm font-semibold leading-6 text-blue-700 hover:text-blue-900 flex items-center gap-1">
                    Contact us to learn more <span aria-hidden="true">→</span>
                  </a>
                </div>
              </div>
              
              {/* Mixed Geometry Image */}
              <div className="lg:col-span-7 order-1 lg:order-2 relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-[2rem] opacity-20 blur-2xl -z-10" />
                <img
                  src="/assets/images/general/river-east-elementary-school-new-york-ny-3-schoolphoto.jpg"
                  alt="Students learning"
                  className="rounded-[2rem] shadow-2xl object-cover w-full h-full transform transition-transform hover:scale-[1.01] duration-500"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats & Values - Bento Grid */}
        <section className="bg-white py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Our Core Values</h2>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {/* Stats Card (Large) */}
              <div className="rounded-3xl bg-blue-900 p-8 sm:p-10 text-white sm:col-span-2 lg:col-span-3 lg:flex lg:items-center lg:justify-around relative overflow-hidden">
                {stats.map((stat) => (
                  <div key={stat.label} className="relative z-10 text-center py-4 lg:py-0">
                    <div className="text-4xl font-bold tracking-tight sm:text-5xl">{stat.value}</div>
                    <div className="mt-1 text-sm font-medium text-blue-100 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Value Cards */}
              {values.map((value, idx) => (
                <div key={value.title} className={`rounded-3xl p-8 ring-1 ring-gray-200 transition-all hover:shadow-lg hover:-translate-y-1 ${idx === 1 ? 'bg-blue-50 ring-blue-100' : 'bg-white'}`}>
                  <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600/10 text-blue-700">
                    {value.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{value.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Info & Events - Asymmetric Grid */}
        <section id="info" className="py-24 bg-gray-50">
           <div className="mx-auto max-w-7xl px-6 lg:px-8">
             <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
               
               {/* Events Column */}
               <div className="lg:col-span-2 space-y-8">
                 <div className="flex items-end justify-between">
                   <h2 className="text-3xl font-bold tracking-tight text-gray-900">Upcoming Events</h2>
                   <a href="https://www.schools.nyc.gov/calendar/2026-2027-school-year-calendar" className="text-sm font-semibold text-blue-700 hover:text-blue-900">
                     View Calendar <span aria-hidden="true">&rarr;</span>
              </a>
            </div>

                 <div className="grid gap-4 sm:grid-cols-2">
              {events.map((event, idx) => (
                     <div key={idx} className="relative flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
                        <div className="flex items-center gap-4">
                          <div className="flex-none rounded-lg bg-blue-50 px-3 py-2 text-center">
                            <div className="text-xs font-bold uppercase text-blue-700">{event.date.month}</div>
                            <div className="text-xl font-bold text-gray-900">{event.date.day}</div>
                    </div>
                          <div>
                            <h3 className="text-base font-semibold text-gray-900 leading-snug">{event.title}</h3>
                            {event.time && (
                              <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                                <CalendarIcon className="h-3.5 w-3.5" />
                                {event.time}
                              </div>
                            )}
                    </div>
                  </div>
                        {event.description && (
                          <p className="text-sm text-gray-600 pl-16 -mt-2">{event.description}</p>
                        )}
                </div>
              ))}
                 </div>
            </div>

               {/* Announcements / News Column */}
               <div className="lg:col-span-1">
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-8">Latest News</h2>
                  <div className="space-y-4">
                    {announcements.map((item) => (
                      <article key={item.title} className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md">
                        <time className="text-xs font-bold uppercase text-cyan-600">{item.date}</time>
                        <h3 className="mt-2 text-lg font-bold text-gray-900 leading-tight">{item.title}</h3>
                        <p className="mt-2 text-sm text-gray-600 line-clamp-3">{item.excerpt}</p>
                      </article>
                    ))}
            </div>
          </div>

            </div>
          </div>
        </section>

        {/* Staff & Contact - Dark/Light Contrast */}
        <section id="staff" className="relative overflow-hidden bg-white py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">

            {/* Full Staff Directory */}
            <div className="mb-20">
              <div className="mx-auto max-w-3xl text-center mb-12">
                <span className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-cyan-700 ring-1 ring-cyan-100">
                  <UserIcon className="h-4 w-4" />
                  Our People
                </span>
                <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                  Staff Directory
                </h2>
                <p className="mt-4 text-lg leading-8 text-gray-600">
                  Meet the educators and staff who make River East Elementary a wonderful place to learn. School year 2026-2027.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {staffDirectory.map((group) => (
                  <div
                    key={group.department}
                    className="rounded-2xl border border-gray-200 bg-gray-50 p-6"
                  >
                    <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-gray-900">
                      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-blue-600 text-white">
                        <UserIcon className="h-4 w-4" />
                      </span>
                      <span>
                        {group.department}
                        {group.room && (
                          <span className="block text-xs font-medium uppercase tracking-wide text-gray-600">{group.room}</span>
                        )}
                      </span>
                    </h3>
                    <ul className="space-y-2.5">
                      {group.members.map((member, idx) => (
                        <li key={`${group.department}-${idx}`} className="text-sm leading-snug">
                          <span className="block font-semibold text-gray-800">{member.name}</span>
                          <span className="block text-xs font-medium uppercase tracking-wide text-blue-600">
                            {member.role}
                            {member.room && (
                              <span className="text-gray-600"> &middot; {member.room}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Family Orientation Presentation */}
              <div className="mt-8 rounded-2xl border border-blue-200 bg-blue-50 p-6 sm:p-8">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
                      <BookIcon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Family Orientation 2026-2027</h3>
                      <p className="mt-1 text-sm leading-6 text-gray-700">
                        Our welcome presentation for families &mdash; school routines, arrival and dismissal, staff introductions, and how to stay connected.
                      </p>
                    </div>
                  </div>
                  <a
                    href="/assets/documents/family-orientation-2026-2027.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Family Orientation 2026-2027 presentation (PDF, opens in a new tab)"
                    className="inline-flex flex-none items-center justify-center gap-2 rounded-full bg-blue-700 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
                  >
                    View Presentation
                    <span aria-hidden="true">&darr;</span>
                  </a>
                </div>
              </div>
            </div>

            <div className="grid gap-12 lg:grid-cols-2">

              {/* Staff List */}
              <div>
                <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-8">People to Know</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {keyStaff.map((person) => (
                    <div key={person.email} className="rounded-2xl border border-gray-100 bg-gray-50 p-6 transition-colors hover:bg-white hover:shadow-md hover:border-blue-100">
                <div className="flex items-center gap-3">
                         <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                           <UserIcon className="h-5 w-5" />
                  </div>
                  <div>
                           <h3 className="text-sm font-bold text-gray-900">{person.name}</h3>
                           <p className="text-xs text-gray-600">{person.role}</p>
                         </div>
                       </div>
                       <a href={`mailto:${person.email}`} className="mt-4 block text-xs font-semibold text-blue-700 hover:text-blue-900">
                         {person.email}
                       </a>
                  </div>
                  ))}
                </div>
              </div>

              {/* Contact Info Card */}
              <div className="relative rounded-3xl bg-gray-900 px-6 py-10 shadow-xl sm:px-12 lg:py-12 overflow-hidden">
                 <div className="relative z-10">
                   <h3 className="text-2xl font-bold text-white">Contact Us</h3>
                   <ul className="mt-6 space-y-6 text-white/80">
                      <li className="flex items-start gap-4">
                        <PhoneIcon className="h-6 w-6 flex-shrink-0 text-cyan-400" />
              <div>
                          <p className="font-semibold text-white">Phone</p>
                          <a href="tel:+12128023035" className="mt-1 block hover:text-white transition-colors">(212) 802-3035</a>
                        </div>
                  </li>
                      <li className="flex items-start gap-4">
                         <MapPinIcon className="h-6 w-6 flex-shrink-0 text-cyan-400" />
                         <div>
                           <p className="font-semibold text-white">Address</p>
                           <p className="mt-1">2351 1st Avenue<br/>New York, NY 10035</p>
                         </div>
                  </li>
                      <li className="flex items-start gap-4">
                         <div className="h-6 w-6 flex items-center justify-center">
                            <span className="text-cyan-400 font-bold">@</span>
              </div>
              <div>
                           <p className="font-semibold text-white">Email</p>
                           <a href="mailto:mpanetta@schools.nyc.gov" className="mt-1 block hover:text-white transition-colors break-all">mpanetta@schools.nyc.gov</a>
                         </div>
                  </li>
                </ul>
              </div>
            </div>

            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
